import { type ReactElement, memo, useCallback, useState } from "react";

import { useApolloClient, useMutation } from "@apollo/client";

import { useAddOrUpdateConnections } from "../../connections/hooks";
import { GroupConnection } from "../../connections/models";
import { ImmutableSetOperations } from "../../core/collections/ImmutableSet";
import { BaseModel } from "../../core/models";
import { createGroupV2 } from "../../dashboard/api/mutations";
import {
    type CreateGroupV2Mutation,
    type CreateGroupV2MutationVariables,
} from "../../dashboard/api/mutations/createGroupV2.gql-generated";
import { type NewGroup, toMutationVariables } from "../../dashboard/models";
import { GroupCache } from "../api/cache";
import { GroupsPagination } from "../api/cache/GroupsPagination";

export interface CreateGroupAPI {
    createGroup: (newGroup: NewGroup) => void;
    groupIsCreated: boolean;
    newGroups: ReadonlySet<string>;
}

export interface CreateGroupContainerV2Props {
    children(api: CreateGroupAPI): ReactElement;
}

export const CreateGroupContainerV2 = memo<CreateGroupContainerV2Props>((props) => {
    const [newGroups, setNewGroups] = useState<ReadonlySet<string>>(new Set());
    const { cache } = useApolloClient();

    const [createNewGroup, { loading: groupCreationInProgress }] = useMutation<
        CreateGroupV2Mutation,
        CreateGroupV2MutationVariables
    >(createGroupV2, {
        update: GroupCache.addGroup,
    });

    const { addOrUpdateConnections, loading: connectionsCreationInProgress } = useAddOrUpdateConnections();

    const createGroupCallback = useCallback<(newGroup: NewGroup) => void>(
        async (newGroup: NewGroup) => {
            try {
                const { data } = await createNewGroup({ variables: toMutationVariables(newGroup) });
                const groupId = data?.saveGroupV2?.group?.id as string;
                setNewGroups(ImmutableSetOperations.add(newGroups, groupId));
                await addOrUpdateConnections(
                    groupId,
                    newGroup.lenses.map((lens) =>
                        BaseModel.createInstance(GroupConnection, {
                            lensCompoundId: lens.compoundId,
                        }),
                    ) as readonly GroupConnection[],
                );
                GroupsPagination.cache.evict(cache);
            } catch (error) {
                console.info(error);
            }
        },
        [createNewGroup, newGroups, addOrUpdateConnections, cache],
    );

    return props.children({
        createGroup: createGroupCallback,
        groupIsCreated: groupCreationInProgress || connectionsCreationInProgress,
        newGroups,
    });
});
